import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";
import { usePageTitle } from "@/hooks/use-page-title";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const COMMON_PASSWORDS_FRONTEND = new Set([
  "password", "password1", "password123", "password1234", "password12345",
  "12345678", "123456789", "1234567890", "11111111", "00000000",
  "qwerty123", "qwertyui", "letmein1", "welcome1", "iloveyou",
  "sunshine1", "princess1", "football", "baseball1", "monkey123",
  "superman1", "batman123", "dragon123", "master123", "admin123",
  "abc12345", "abc123456", "trustno1", "shadow123", "hello123",
]);

function getPasswordStrength(pwd: string): { level: "weak" | "fair" | "strong"; label: string; color: string } {
  if (pwd.length === 0) return { level: "weak", label: "", color: "" };
  if (pwd.length < 8 || COMMON_PASSWORDS_FRONTEND.has(pwd.toLowerCase())) {
    return { level: "weak", label: "Weak", color: "#ef4444" };
  }
  const hasLower = /[a-z]/.test(pwd);
  const hasUpper = /[A-Z]/.test(pwd);
  const hasDigit = /\d/.test(pwd);
  const hasSymbol = /[^a-zA-Z0-9]/.test(pwd);
  const variety = [hasLower, hasUpper, hasDigit, hasSymbol].filter(Boolean).length;
  if (pwd.length >= 12 || (pwd.length >= 8 && variety >= 3)) {
    return { level: "strong", label: "Strong", color: "#10b981" };
  }
  return { level: "fair", label: "Fair", color: "#f59e0b" };
}

export default function ResetPasswordPage() {
  usePageTitle("Reset Password");
  const [, navigate] = useLocation();
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [tokenError, setTokenError] = useState(false);

  if (!token) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-red-50">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <CardTitle className="text-2xl">Invalid reset link</CardTitle>
            <CardDescription>
              This password reset link is missing or malformed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link href="/forgot-password" className="text-sm text-blue-600 hover:underline font-medium">
              Request a new reset link
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-red-50">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <CardTitle className="text-2xl">Link expired or already used</CardTitle>
            <CardDescription>
              This password reset link is no longer valid. Reset links expire after 1 hour and can only be used once.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link href="/forgot-password" className="text-sm text-blue-600 hover:underline font-medium">
              Request a new reset link
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${BASE}/api/auth/reset-password`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 400 && (data.error?.includes("expired") || data.error?.includes("invalid") || data.error?.includes("Invalid"))) {
          setTokenError(true);
          return;
        }
        setError(data.error ?? "Something went wrong. Please try again.");
        return;
      }
      navigate("/login?passwordReset=1");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const strength = getPasswordStrength(password);

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="absolute top-4 left-4">
        <Link href="/login" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to sign in
        </Link>
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex items-center justify-center">
            <img src="/logo-new.png" alt="QuoTrak" style={{ height: "4rem", width: "auto", objectFit: "contain" }} />
          </div>
          <CardTitle className="text-2xl">Set a new password</CardTitle>
          <CardDescription>
            Enter and confirm your new password below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 characters"
                required
                minLength={8}
                autoComplete="new-password"
                autoFocus
              />
              {password.length > 0 && (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {(["weak", "fair", "strong"] as const).map((level, i) => {
                      const levels = ["weak", "fair", "strong"];
                      const currentIndex = levels.indexOf(strength.level);
                      const filled = i <= currentIndex;
                      return (
                        <div
                          key={level}
                          style={{
                            flex: 1,
                            height: "4px",
                            borderRadius: "2px",
                            background: filled ? strength.color : "hsl(var(--border))",
                            transition: "background 0.2s",
                          }}
                        />
                      );
                    })}
                  </div>
                  {strength.label && (
                    <p style={{ fontSize: "0.75rem", color: strength.color, fontWeight: 500 }}>
                      {strength.label}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your new password"
                required
                autoComplete="new-password"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reset Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
